import clsx from 'clsx';
import { FC } from 'react';

import { WithClassName } from '../../../types/react';
import { MastercardWordmarkIcon, VisaWordmarkIcon } from '../../icons/paySystems';
import { PciDssIcon } from '../../icons/PciDssIcon';
import { Container } from '../common/Container';
import { Flex } from '../common/Flex';
import { Logo } from '../common/Logo';
import { LogoText } from '../common/LogoText';
import { Text } from '../common/Text';
import { TextLink } from '../common/TextLink';

import styles from './Footer.module.scss';

type FooterProps = WithClassName & {};

const BADGE_HEIGHT = 48;
const BADGE_WIDTH = Math.round((142 / 69) * BADGE_HEIGHT);

const SECTIONS: {
  title: string;
  links: { title: string; link: string; target?: string; isOutsideLink?: boolean }[];
}[] = [
  {
    title: 'Company',
    links: [
      {
        title: 'Why SmartGates',
        link: '/why-sg',
      },
      {
        title: 'Integrations',
        link: '/integrations',
      },
    ],
  },
  {
    title: 'Documents',
    links: [
      {
        title: 'Terms of use',
        link: '/doc/terms-of-use',
        target: '_blank',
      },
      {
        title: 'Privacy policy',
        link: '/doc/privacy',
        target: '_blank',
      },
      {
        title: 'KYC policy',
        link: '/doc/kyc',
        target: '_blank',
      },
    ],
  },
  {
    title: 'Developers',
    links: [
      {
        title: 'API reference',
        link: '/docs',
        isOutsideLink: true,
      },
    ],
  },
  {
    title: 'Contacts',
    links: [
      {
        title: 'Support',
        link: 'mailto:support@smart-gates.net',
      },
    ],
  },
];

export const Footer: FC<FooterProps> = (props) => {
  const { className } = props;

  return (
    <footer className={clsx(styles.root, className)}>
      <Container>
        <div className={styles.grid}>
          {SECTIONS.map(({ title, links }, index) => (
            <Flex className={styles.section} key={index} direction="column">
              <Text type="medium18">{title}</Text>

              {links.map(({ title, link, target, isOutsideLink }, index) => (
                <TextLink key={index} href={link} target={target} isOutsideLink={isOutsideLink} variant="white-50p">
                  {title}
                </TextLink>
              ))}
            </Flex>
          ))}
        </div>
      </Container>

      <Container>
        <div className={styles.badgesInner}>
          <Flex
            className={styles.badgesRow}
            alignItems="center"
            justifyContent="flexStart"
            wrap="wrap"
            gap={8}
            childMinSize="unset"
          >
            <VisaWordmarkIcon className={styles.badgeIcon} width={BADGE_WIDTH} height={BADGE_HEIGHT} />
            <MastercardWordmarkIcon className={styles.badgeIcon} width={BADGE_WIDTH} height={BADGE_HEIGHT} />
            <PciDssIcon className={styles.badgeIcon} width={BADGE_WIDTH} height={BADGE_HEIGHT} />
          </Flex>
        </div>
      </Container>

      <Container>
        <Flex
          className={styles.bottomRow}
          alignItems="center"
          justifyContent="spaceBetween"
          wrap="wrap"
          gap={4}
          childMinSize="0"
        >
          <Flex className={styles.logoGroup} gap={3} alignItems="center" childMinSize="unset">
            <LogoText />

            <Logo />
          </Flex>

          <Text className={styles.companyBlock} type="regular12" color="white50">
            LEAD GATES LTD. UIC: 207064979. Apt. 20, Fl. 6, 14 Miziya Str., Town of Plovdiv, Republic of Bulgaria{' '}
            support@smart-gates.net
          </Text>
        </Flex>
      </Container>
    </footer>
  );
};
